import { type ToastApi, toastApiRef, useApi } from '@backstage/frontend-plugin-api';
import { TemplateCard, type TemplateCardProps } from '@backstage/plugin-scaffolder-react/alpha';
import { type ComponentType, type KeyboardEvent, type MouseEvent, useCallback } from 'react';
import {
  getTemplateDisplayName,
  isUnsupportedTemplate,
  UNSUPPORTED_ACTIONS,
  UNSUPPORTED_FIELDS,
  UNSUPPORTED_TEMPLATE_MESSAGE,
} from './unsupportedTemplates';
import { trackScaffolderTemplateSelected } from './trackScaffolderTemplateSelected';
import './disabledTemplateCard.css';

type DisabledTemplateCardBaseProps = TemplateCardProps & {
  toastApi: Pick<ToastApi, 'post'>;
  CardComponent?: ComponentType<any>;
};

export const DisabledTemplateCardBase = ({
  CardComponent = TemplateCard,
  toastApi,
  template,
  onSelected,
  ...cardProps
}: DisabledTemplateCardBaseProps) => {
  const isDisabled = isUnsupportedTemplate(template);
  const disabledLabel = `${getTemplateDisplayName(template)} is disabled. ${UNSUPPORTED_TEMPLATE_MESSAGE}`;

  const showDisabledToast = useCallback(() => {
    toastApi.post({
      title: `${getTemplateDisplayName(template)} is disabled`,
      description: (
        <div className="disabledTemplateCard__toastDescription">
          <div>This template uses deprecated Code-spawn actions or field extensions and cannot be launched.</div>
          <div className="disabledTemplateCard__toastMeta">
            <strong>Actions:</strong> {UNSUPPORTED_ACTIONS.join(', ')}
          </div>
          <div className="disabledTemplateCard__toastMeta">
            <strong>Fields:</strong> {UNSUPPORTED_FIELDS.join(', ')}
          </div>
        </div>
      ),
      status: 'warning',
      timeout: 7000,
    });
  }, [template, toastApi]);

  const handleBlockedClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      showDisabledToast();
    },
    [showDisabledToast],
  );

  const handleBlockedKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      showDisabledToast();
    },
    [showDisabledToast],
  );

  const handleSelected = useCallback(() => {
    // Product analytics: which Create templates are chosen most (Datadog RUM).
    // See https://github.com/cfacorp/homestead/issues/355
    trackScaffolderTemplateSelected(template);
    onSelected?.(template);
  }, [onSelected, template]);

  return (
    <div
      className={`disabledTemplateCard${isDisabled ? ' disabledTemplateCard--disabled' : ''}`}
      onClick={isDisabled ? handleBlockedClick : undefined}
      onKeyDown={isDisabled ? handleBlockedKeyDown : undefined}
      tabIndex={isDisabled ? 0 : undefined}
      role={isDisabled ? 'button' : undefined}
      aria-label={isDisabled ? disabledLabel : undefined}
      title={isDisabled ? UNSUPPORTED_TEMPLATE_MESSAGE : undefined}
    >
      <div className="disabledTemplateCard__inner" {...(isDisabled ? { inert: '' } : {})}>
        <CardComponent template={template} onSelected={handleSelected} {...cardProps} />
      </div>
      {isDisabled && (
        <span className="disabledTemplateCard__badge" aria-hidden="true">
          Disabled
        </span>
      )}
    </div>
  );
};

export const DisabledTemplateCard = (props: TemplateCardProps) => {
  const toastApi = useApi(toastApiRef);

  return <DisabledTemplateCardBase {...props} toastApi={toastApi} />;
};
